import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import {
  ACTIVITY_LOG_APP_BASE_URL,
  ACTIVITY_LOG_APP_NAME,
  CONTACT_RESEARCH_APP_NAME,
  CONTACT_RESEARCH_BASE_URL,
  GLOBALSEARCH_APP_NAME,
  GLOBALSEARCH_BASE_URL,
  GROUP_APP_NAME,
  GROUP_BASE_URL,
  HOME_APP_BASE_URL,
  HOME_APP_NAME,
  INTERNAL_COMMS_BASE_URL,
  INTERNAL_COMMUNICATIONS_APP_NAME,
  Module,
  ModuleType,
  OPPORTUNITIES_APP_BASE_URL,
  OPPORTUNITIES_APP_NAME,
  ORG_APP_NAME,
  ORG_BASE_URL,
  PATIENT_PROFILE_APP_BASE_URL,
  PATIENT_PROFILE_APP_NAME,
  PC_APP_NAME,
  PC_BASE_URL,
  PREF_MGT_APP_NAME,
  PREF_MGT_BASE_URL,
  REPORTS_APP_BASE_URL,
  REPORTS_APP_NAME,
  RXHUB_APP_NAME,
  RXHUB_BASE_URL,
  STORE_APP_NAME,
  STORE_BASE_URL,
  SpinnerService,
  USER_ADMIN_APP_NAME,
  USER_ADMIN_BASE_URL
} from '@enliven/angular';
import { SessionTransfer } from '../models/session-transfer.model';
import { E360SessionService } from './e360-session.service';
import { UrlParamService } from './url-param.service';

// Tech Debt: move to common library
export interface DeepLink {
  function: string;
  id: string;
  organizationId: string;
}

export interface NavigationOptions {
  defaultNavigation?: boolean;
  deepLink?: DeepLink;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly router: Router = inject(Router);
  private readonly spinnerService: SpinnerService = inject(SpinnerService);
  private readonly urlParams: UrlParamService = inject(UrlParamService);

  adminModuleActive: WritableSignal<boolean> = signal(false);

  navigate(nextModule: Module, options?: NavigationOptions) {
    if (nextModule.type === ModuleType.EMBEDDED) {
      this.embeddedApp(nextModule, options);
    } else {
      this.topLevelApp(nextModule, options);
    }
  }

  private embeddedApp(nextModule: Module, options?: NavigationOptions) {
    const targetBaseUrl: string = this.getEmbeddedAppBaseUrl(nextModule.name);
    if (!targetBaseUrl) return;

    // `this.router.url` always starts with `/app/currentEmbeddedApp`. There may be additional segments, but the first two are guaranteed.
    // Grab segment `[1]` directly — do NOT use `pop()`, as that could possibly pull a plugin app's route piece
    const currentEmbeddedApp: string = this.router.url.split('/').filter(Boolean)[1];

    // part of fix for spinner issue (IP-14420)
    if (!options?.deepLink && currentEmbeddedApp === targetBaseUrl) return;

    this.spinnerService.setSpinner(true);
    nextModule.active = true;
    this.adminModuleActive.set(nextModule.management);

    let queryParams: Params;
    const { moduleId, functionName, functionId } = this.urlParams;

    if (options?.deepLink?.function) {
      queryParams = {
        function: options.deepLink.function
      };

      if (options.deepLink.id) {
        queryParams['id'] = options.deepLink.id;
      }
    } else if (moduleId === nextModule.id && options?.defaultNavigation && functionName) {
      queryParams = {
        function: functionName
      };

      if (functionId) {
        queryParams['id'] = functionId;
      }
    }

    const navExtras: NavigationExtras = queryParams ? { queryParams } : undefined;

    this.router.navigate(['app', targetBaseUrl], navExtras);
  }

  private getEmbeddedAppBaseUrl(name: string): string {
    switch (name) {
      case USER_ADMIN_APP_NAME:
        return USER_ADMIN_BASE_URL;
      case PC_APP_NAME:
        return PC_BASE_URL;
      case ORG_APP_NAME:
        return ORG_BASE_URL;
      case STORE_APP_NAME:
        return STORE_BASE_URL;
      case GROUP_APP_NAME:
        return GROUP_BASE_URL;
      case PREF_MGT_APP_NAME:
        return PREF_MGT_BASE_URL;
      case CONTACT_RESEARCH_APP_NAME:
        return CONTACT_RESEARCH_BASE_URL;
      case OPPORTUNITIES_APP_NAME:
        return OPPORTUNITIES_APP_BASE_URL;
      case REPORTS_APP_NAME:
        return REPORTS_APP_BASE_URL;
      case HOME_APP_NAME:
        return HOME_APP_BASE_URL;
      case ACTIVITY_LOG_APP_NAME:
        return ACTIVITY_LOG_APP_BASE_URL;
      case PATIENT_PROFILE_APP_NAME:
        return PATIENT_PROFILE_APP_BASE_URL;
      case INTERNAL_COMMUNICATIONS_APP_NAME:
        return INTERNAL_COMMS_BASE_URL;
      case GLOBALSEARCH_APP_NAME:
        return GLOBALSEARCH_BASE_URL;
      case RXHUB_APP_NAME:
        return RXHUB_BASE_URL;
      default:
        return '';
    }
  }

  private topLevelApp(module: Module, options?: NavigationOptions) {
    this.e360SessionService.createE360SessionTransfer().subscribe((sessionTransfer: SessionTransfer) => {
      const { moduleId, functionName, functionId } = this.urlParams;

      let { url } = module;
      const { organizationId, sessionTransferId } = sessionTransfer;
      url = url.replace('<TOKENID>', sessionTransferId).replace('<ORGANIZATION>', organizationId);

      if (moduleId !== module.id || !options?.defaultNavigation || !functionName) {
        window.open(url, '_blank');
        return;
      }

      url = `${url}&function=${functionName}`;

      if (functionId) {
        url = `${url}&id=${functionId}`;
      }

      window.open(url, '_blank');
    });
  }
}
